import {Box, Stack, Typography} from '@mui/material';
import {DataGrid, GridColDef, GridRowSelectionModel} from '@mui/x-data-grid';
import {useMemo, useState, useEffect} from 'react';

import {AccessGroup, OrgUser} from 'src/apis/autoGenerate/types';
import Modal from 'src/components/Modal';
import SearchInput from 'src/components/SearchInput';
import {formatDate} from 'src/utils/common';
import {orgUserServiceFind} from 'src/apis/autoGenerate/services';
import {useOrgId} from 'src/hooks';

interface AddOrgUsersToAccessGroupModalProps {
  open: boolean;
  handleConfirm: (orgUserIds: string[]) => void;
  handleCancel: VoidFunction;
  targetGroup: AccessGroup;
  existingOrgUsers?: OrgUser[];
}

export default function AddOrgUsersToAccessGroupModal({
  open,
  handleConfirm,
  handleCancel,
  targetGroup,
  existingOrgUsers = [],
}: AddOrgUsersToAccessGroupModalProps) {
  const [selectedRowIds, setSelectedRowIds] = useState<GridRowSelectionModel>([]);
  const [loadingOrgUsers, setLoadingOrgUsers] = useState(false);
  const [orgUsers, setOrgUsers] = useState<OrgUser[]>([]);
  const [filteredOrgUsers, setFilteredOrgUsers] = useState<OrgUser[]>([]);
  const {orgId} = useOrgId();

  useEffect(() => {
    if (open && orgId) {
      getOrgUsers();
    }
  }, [open, orgId]);

  // Filter out org users that are already in the access group
  useEffect(() => {
    if (orgUsers.length) {
      if (existingOrgUsers && existingOrgUsers.length) {
        // Create a set of existing org user IDs for faster lookup
        const existingOrgUserIds = new Set(existingOrgUsers.map(orgUser => orgUser.id));

        // Filter the org users list to exclude those already in the group
        const filtered = orgUsers.filter(orgUser => !existingOrgUserIds.has(orgUser.id));
        setFilteredOrgUsers(filtered);
      } else {
        setFilteredOrgUsers(orgUsers);
      }
    } else {
      setFilteredOrgUsers([]);
    }
  }, [orgUsers, existingOrgUsers]);

  const getOrgUsers = async (name?: string) => {
    setLoadingOrgUsers(true);
    try {
      const res = await orgUserServiceFind(name, orgId, 500, 1);
      const _orgUsers = res.data?.users || [];
      setOrgUsers(_orgUsers);
    } catch (error) {
      console.error('Failed to load org users:', error);
    } finally {
      setLoadingOrgUsers(false);
    }
  };

  const handleAddOrgUsers = () => {
    if (selectedRowIds.length > 0) {
      handleConfirm(selectedRowIds as string[]);
    }
  };

  const handleSearchOrgUserByInfo = (name: string) => {
    getOrgUsers(name);
  };

  const columns: GridColDef[] = useMemo(() => {
    return [
      {
        field: 'name',
        headerName: 'Name',
        width: 220,
        renderCell: val => {
          return (
            <Stack>
              <Typography variant="body2" sx={{fontSize: 14, fontWeight: 500}}>
                {val.value || ''}
              </Typography>
              <Typography variant="body2" sx={{color: theme => theme.palette.text.secondary}}>
                {val.row.email || ''}
              </Typography>
            </Stack>
          );
        },
      },
      {
        field: 'role',
        headerName: 'Role',
        width: 150,
        renderCell: val => {
          return <Box>{val.value?.name || ''}</Box>;
        },
      },
      {
        field: 'createdAt',
        headerName: 'Create Date',
        width: 150,
        renderCell: val => {
          return <Box>{formatDate(val.value)}</Box>;
        },
      },
    ];
  }, []);

  return (
    <Modal
      open={open}
      title={`Assign Org Users to ${targetGroup.name}`}
      handleCancel={handleCancel}
      handleConfirm={handleAddOrgUsers}
      modalContent={
        <Box position={'relative'} height={'70vh'}>
          <Box sx={{position: 'fixed', zIndex: 2, backgroundColor: '#fff'}}>
            <SearchInput placeholder="Search Org User" onSearch={handleSearchOrgUserByInfo} />
          </Box>
          <Box
            sx={{position: 'absolute', height: 'calc(70vh - 50px)', overflow: 'scroll', top: 50}}
          >
            <DataGrid
              loading={loadingOrgUsers}
              disableColumnFilter
              disableColumnMenu
              disableColumnSelector
              rows={filteredOrgUsers.length ? filteredOrgUsers : []}
              columns={columns}
              checkboxSelection
              initialState={{
                pagination: {
                  paginationModel: {page: 0, pageSize: 10},
                },
              }}
              onRowSelectionModelChange={res => {
                setSelectedRowIds(res);
              }}
              slots={{
                noRowsOverlay: () => (
                  <Box textAlign="center" my={2}>
                    No Org Users
                  </Box>
                ),
              }}
              pageSizeOptions={[5, 10]}
            />
          </Box>
        </Box>
      }
    />
  );
}
